import React from 'react';
import * as Slider from '@radix-ui/react-slider';
import {Input, InputType, IInputProps} from '~/components/Controls/Input';
import {FormField, hasError, IFieldProps} from '~/components/Form';
import {Intent} from '~/lib/intent';
import style from '~/modules/BusinessIncentives/components/PercentageField.module.scss';

interface IPercentageFieldOptions extends IInputProps {
    sliderMin?: number;
    sliderMax?: number;
    sliderStep?: number;
    sliderMarks?: number[];
    sliderMarkStep?: number;
    quickValues?: number[];
}

const clamp = (value: number, min: number, max: number): number => Math.min(max, Math.max(min, value));

const parsePercentInput = (value: string): number | '' => {
    if (!value.trim()) {
        return '';
    }
    const normalized = value.replace(',', '.').replace(/[^\d.-]/g, '');
    const parsed = Number.parseFloat(normalized);
    if (!Number.isFinite(parsed)) {
        return '';
    }
    return parsed;
};

const buildMarks = (min: number, max: number, step: number): number[] => {
    if (!Number.isFinite(min) || !Number.isFinite(max) || !Number.isFinite(step) || step <= 0 || max < min) {
        return [min, max].filter(value => Number.isFinite(value));
    }
    const marks: number[] = [];
    let current = min;
    while (current <= max + 1e-9) {
        marks.push(Number.parseFloat(current.toFixed(4)));
        current += step;
    }
    if (marks[marks.length - 1] !== max) {
        marks.push(max);
    }
    return marks;
};

export const PercentageField = (props: IFieldProps<IPercentageFieldOptions>) => {
    const {
        sliderMin = 0,
        sliderMax = 30,
        sliderStep = 1,
        sliderMarks,
        sliderMarkStep = 5,
        quickValues = [2, 3, 4, 5, 6],
        max,
        disabled,
        placeholder = '0',
        min = 0,
        step = 0.01,
        className: inputClassName,
        ...restInputOptions
    } = props.options ?? {};

    const rawValue = props.field.value;
    const minValue = Number(min);
    const sliderMinValue = Number(sliderMin);
    const sliderMaxValue = Number(sliderMax);
    const sliderStepValue = Number(sliderStep);
    const sliderMarkStepValue = Number(sliderMarkStep);
    const maxValue = typeof max === 'number' ? max : sliderMaxValue;
    const numericValue = typeof rawValue === 'number' && Number.isFinite(rawValue)
        ? clamp(rawValue, minValue, maxValue)
        : 0;
    const sliderValue = clamp(numericValue, sliderMinValue, sliderMaxValue);
    const sliderRange = sliderMaxValue - sliderMinValue;
    const markActiveThreshold = Math.max(sliderStepValue / 2, 0.0001);
    const marks = Array.from(new Set([
        ...(sliderMarks ?? buildMarks(sliderMinValue, sliderMaxValue, sliderMarkStepValue))
            .filter(mark => mark >= sliderMinValue && mark <= sliderMaxValue),
        sliderMinValue,
        sliderMaxValue,
    ])).sort((left, right) => left - right);
    const quickSelectValues = Array.from(new Set([
        ...quickValues,
    ]))
        .filter(value => value >= sliderMinValue && value <= sliderMaxValue)
        .sort((left, right) => left - right);
    const hasQuickValues = quickSelectValues.length > 0;
    const inputClasses = [
        inputClassName,
        hasQuickValues ? style.inputWithQuickValues : '',
    ].filter(Boolean).join(' ');

    return (
        <FormField {...props}>
            <div className={style.wrapper}>
                <div className={style.inputRow}>
                    <div className={style.inputWrap}>
                        <Input
                            {...restInputOptions}
                            className={inputClasses}
                            type={InputType.FLOAT}
                            value={rawValue === '' || rawValue === null || rawValue === undefined
                                ? ''
                                : clamp(Number(rawValue) || 0, minValue, maxValue)}
                            placeholder={placeholder}
                            disabled={disabled}
                            intent={hasError(props) && Intent.DANGER}
                            step={step}
                            onChange={(value) => {
                                const parsed = parsePercentInput(value);
                                if (parsed === '') {
                                    props.form.setFieldValue(props.field.name, '');
                                    return;
                                }
                                props.form.setFieldValue(props.field.name, clamp(parsed, minValue, maxValue));
                            }}
                        />
                        {hasQuickValues && (
                            <div className={style.quickValues}>
                                {quickSelectValues.map(value => {
                                    const isActive = Math.abs(numericValue - value) < 0.005;
                                    const displayValue = String(value).replace('.', ',');
                                    return (
                                        <button
                                            key={value}
                                            type='button'
                                            className={[style.quickValue, isActive && style.quickValueActive].filter(Boolean).join(' ')}
                                            disabled={disabled}
                                            onClick={() => props.form.setFieldValue(props.field.name, value)}
                                        >
                                            {displayValue}%
                                        </button>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                </div>
                <div className={style.sliderRow}>
                    <div className={style.sliderArea}>
                        <Slider.Root
                            className={style.sliderRoot}
                            min={sliderMinValue}
                            max={sliderMaxValue}
                            step={sliderStepValue}
                            disabled={disabled}
                            value={[sliderValue]}
                            onValueChange={(values: number[]) => {
                                const nextValue = values[0];
                                if (typeof nextValue === 'number' && Number.isFinite(nextValue)) {
                                    props.form.setFieldValue(
                                        props.field.name,
                                        clamp(nextValue, minValue, maxValue)
                                    );
                                }
                            }}
                        >
                            <Slider.Track className={style.sliderTrack}>
                                <Slider.Range className={style.sliderRange}/>
                            </Slider.Track>
                            <Slider.Thumb className={style.sliderThumb} aria-label='Percentage'/>
                        </Slider.Root>
                        <div className={style.marksRail}>
                            {marks.map(mark => {
                                const relative = sliderRange === 0 ? 0 : (mark - sliderMinValue) / sliderRange;
                                const position = clamp(relative * 100, 0, 100);
                                const isActive = mark <= sliderValue + markActiveThreshold;
                                const markLabel = String(mark).replace('.', ',');

                                return (
                                    <button
                                        key={mark}
                                        type='button'
                                        className={[style.mark, isActive && style.markActive].filter(Boolean).join(' ')}
                                        style={{left: `${position}%`}}
                                        disabled={disabled}
                                        title={`${markLabel}%`}
                                        aria-label={`Set ${markLabel} percent`}
                                        onClick={() => props.form.setFieldValue(props.field.name, mark)}
                                    >
                                        <span className={style.markTick}/>
                                        <span className={style.markLabel}>{markLabel}</span>
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                </div>
            </div>
        </FormField>
    );
};
